import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";

import { boundedNote } from "../board.ts";
import type { Session } from "../session.ts";
import {
  ADD_REFUSAL,
  listingNote,
  SLO_SCOPE,
  sloRow,
  statesPresent,
  stateOf,
  type Slo,
} from "../slo-telemetry.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `slo` in `16 WP15 Platform Machinery.dc.html`: the service objectives, and the telemetry that is
 * in the title and nowhere else.
 *
 * ```
 * slo:['SLOs and telemetry','OBS-01 · OBS-02',
 *   'Service objectives with the error budget left, not just uptime.',
 *   [['SLOs','64','done','met'],['Burning fast','2','failed','budget'],
 *    ['At risk','5','caution','this month'],['Alerts open','3','human','']],
 *   ['Service','Objective','Budget left'],
 *   [['Payment orchestrator','99.9% under 800 ms','12%','failed','burning fast'],
 *    ['Dispatch','99.5% offers in 3 s','61%','done','healthy'],
 *    ['Driver location','99% fresh under 30 s','34%','caution','at risk']],
 *   'warn','A burning budget pages the on-call team; the console only shows it.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                      | source                                                 |
 * | ------------------------------------------ | ------------------------------------------------------ |
 * | the rows                                   | `OBS-02 GET /v1/platform/slos` (ADR 0066)             |
 * | `Service` — `Payment orchestrator`         | `slos.serviceId` as `PAY-01`. No display name exists   |
 * | `Objective` — `99.9%`                      | `targetNumerator / targetDenominator`                  |
 * | `Objective` — `under 800 ms`               | **nothing** — an SLI URN, withheld, and not prose      |
 * | `Budget left` — `12%`                      | `slos.remainingBudgetPermille`, a ratio with no terms  |
 * | `done · healthy`, `caution · at risk`      | the budget against a threshold this board picked       |
 * | `failed · burning fast`                    | **nothing** — no burn rate is computed anywhere        |
 * | `Burning fast · 2`, `Alerts open · 3`      | **nothing** — unmodelled, and alerting is nobody's here |
 * | `SLOs · 64`, `At risk · 5`                 | no listing publishes a total; no tile row here (Q-178) |
 * | anything telemetry                         | **nothing, by decision** — ADR 0065                    |
 * | the primary command                        | drawn and refused — `ADD_REFUSAL`, ADR 0066 §9         |
 *
 * ## One service answers, and the other declined with reasons
 *
 * The source chip names two owners and this component calls one. **OBS-01 publishes nothing for this
 * page and will not** — ADR 0065 — because its whole state is four in-process counters with no
 * tenant field in a horizontally scaled gateway, so a public figure would change between two
 * refreshes without anything having happened, and because the designed screen draws no telemetry
 * element to put one in. That is a decision rather than route work pending, which is why this file
 * has no second listing and no degraded-secondary branch: there is nothing to degrade.
 *
 * ## The page stays degraded with ADR 0066 served, and that is expected
 *
 * Two of the four tiles and one of the three badge states are unmodelled. `burning fast` and
 * `Burning fast · 2` need a burn rate: `evaluateBudget` computes none, `burn` appears nowhere in
 * that service's source, and ADR 0066 §4 withholds the window bounds *with* the event counts, so a
 * client cannot derive one either. `Alerts open · 3` is further still — `service.yaml` puts alert
 * delivery under `non_responsibilities`, so no service in this cohort holds an alert at all. The
 * board draws a two-state badge, omits the third, and says so under the grid; `../slo-telemetry.ts`
 * argues each.
 *
 * ## The budget crosses as a ratio and its terms do not
 *
 * `remainingBudgetPermille` arrives with **no counts behind it**, by decision: the six event counts
 * over an objective's window are one operator's request volume, which on this platform is its order
 * volume, and a window plus a consumed count is a rate that reconstructs it. The design draws `12%`,
 * and a percentage is publishable without its denominator. `null` is not zero and is drawn as the
 * design's own `—`; see `../slo-telemetry.ts`.
 */

const sloManagement = services.observability__slo_management;

export interface SloTelemetryProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Health › SLOs and telemetry`, the design's trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "SLOs and telemetry";
/**
 * The design's own source chip, word for word: `OBS-01 · OBS-02`.
 *
 * **Both codes, although only OBS-02 answers.** It is the design's copy and `CLAUDE.md` §1 governs
 * it; editing it to `OBS-02` would be redrawing the design to match the build, and ADR 0065 §4 is
 * explicit that OBS-01's ownership of this page stands while its absence clause does not. The
 * footnote under the grid says what OBS-01 publishes and why, which is the honest form of the
 * caveat — ADR 0065's own rejected alternative is a caveat in a contract description nobody reads.
 */
const SOURCE = "OBS-01 · OBS-02";
const DESCRIPTION = "Service objectives with the error budget left, not just uptime.";
const COLUMNS = ["Service", "Objective", "Budget left"] as const;
const NOTE = "A burning budget pages the on-call team; the console only shows it.";
/** The design's own `noteK` for this page is `warn`, which its `NK` map draws in the amber tint. */
const NOTE_KIND = "warn" as const;
/** The design's own `FORMS.slo[0]`, which is the word on its heading button. */
const PRIMARY = "Add SLO";

/** The most rows one request answers, which is this listing's own maximum. */
const PER_FETCH = 200;

/**
 * Narrow one published row onto the type the contract describes.
 *
 * The generated **client method** types `remainingBudgetPermille`, `evaluatedAt` and `complete` as
 * `unknown`, because the contract writes their nullability as `type: [number, "null"]` and the
 * generator does not narrow that form; the generated `PlatformSloSummary` type beside it says
 * `number | null`. So the narrowing happens once, here at the edge, by `typeof` rather than by a
 * cast — a cast would assert a guarantee this console did not check, and anything that is not of the
 * declared type is read as the absence the contract's other branch permits.
 *
 * **The absence branch matters on all three fields and is not defensive padding.** A `null` budget
 * is an objective nobody has evaluated and is drawn `—` rather than `0%`; a `null` `complete` is the
 * same absence; and reading either as a number would badge an untested objective as a failing one.
 */
function sloOf(row: {
  tenantId: string;
  sloId: string;
  serviceId: string;
  name: string;
  targetNumerator: number;
  targetDenominator: number;
  windowDays: number;
  version: number;
  remainingBudgetPermille: unknown;
  evaluatedAt: unknown;
  complete: unknown;
}): Slo {
  return {
    tenantId: row.tenantId,
    sloId: row.sloId,
    serviceId: row.serviceId,
    name: row.name,
    targetNumerator: row.targetNumerator,
    targetDenominator: row.targetDenominator,
    windowDays: row.windowDays,
    version: row.version,
    remainingBudgetPermille:
      typeof row.remainingBudgetPermille === "number" ? row.remainingBudgetPermille : null,
    evaluatedAt: typeof row.evaluatedAt === "string" ? row.evaluatedAt : null,
    complete: typeof row.complete === "boolean" ? row.complete : null,
  };
}

export function SloTelemetry({ transport, session, crumbs }: SloTelemetryProps): ReactNode {
  const [rows, setRows] = useState<readonly Slo[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * The scope asked for before the call, not discovered from a refusal.
     *
     * One state naming it, because that is what an operator can act on: they can ask their
     * administrator for `slo:platform:read` by name. A 403 from the service renders the same state,
     * so this is not the only guard; it is the one that makes the scope a literal in this
     * application's source, which is what `channel-scopes.mjs` derives the Keycloak grant from.
     * ADR 0049 §3.
     */
    if (!session.scopes.has(SLO_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [SLO_SCOPE] });
      return;
    }

    /*
     * `listPlatformSlos` — the platform operation, not the tenant-bounded by-identifier read.
     *
     * OBS-02's four others are all `/internal/v1` with a `tenantId` on the path, and the one a board
     * might reach for is `getSlo`, which needs the `sloId` this listing exists to discover. There is
     * no second call here: OBS-01 publishes nothing for this page, by decision (ADR 0065).
     */
    try {
      const page = await sloManagement
        .createSloManagementClient(transport)
        .listPlatformSlos(String(PER_FETCH));
      setRows(page.slos.map(sloOf));
      setTruncated(typeof page.nextAfter === "string");
    } catch (error) {
      setFailure(stateForError(error, [SLO_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter((slo) => state === "" || stateOf(slo).label === state).map(sloRow);

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "service objectives",
    "ordered by tenant, then objective",
  );
  const footnote = listingNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No service objective is defined on any tenant. A row appears here when an operator defines one with OBS-02."
      primary={{ label: PRIMARY, refusal: ADD_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "warning", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(footnote === undefined ? {} : { footnote })}
      exportName="slos-telemetry"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
